import { useState, useEffect } from 'react';
import { saveRegistration, getRegistrationCount, checkDuplicateRegistration } from '../services/registrationService';
import { isRegistrationOpen } from '../config/events';

/**
 * Shown after a successful registration when other events are also accepting signups.
 * Reuses the already-verified phone/userId and collected form data — no second OTP.
 * @param {Object[]} events - other active registration events (excluding the one just completed)
 * @param {string} userId - Firebase Auth UID from the just-completed registration
 * @param {Object} formData - sanitized form data from the just-completed registration
 */
const CrossSellPrompt = ({ events, userId, formData }) => {
  const [openEvents, setOpenEvents] = useState(null); // null = still checking capacity
  const [selected, setSelected] = useState({});
  const [results, setResults] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');

  useEffect(() => {
    // Filter out events that are full or that this phone number already registered
    // for (e.g. on a prior visit) — otherwise offering it looks like a bug when the
    // "register" click silently does nothing new.
    const checkAll = async () => {
      const checks = await Promise.all(events.map(async (e) => {
        const [count, isDuplicate] = await Promise.all([
          getRegistrationCount(e.id),
          checkDuplicateRegistration(formData.phoneNumber, e.id)
        ]);
        return isRegistrationOpen(e) && count < e.registration.cap && !isDuplicate;
      }));
      setOpenEvents(events.filter((e, i) => checks[i]));
    };
    checkAll();
  }, [events, formData.phoneNumber]);

  if (openEvents === null || openEvents.length === 0) return null;

  const toggle = (eventId) => {
    setSelected((prev) => ({ ...prev, [eventId]: !prev[eventId] }));
  };

  const handleSubmit = async () => {
    const toRegister = openEvents.filter((e) => selected[e.id] && !results[e.id]);
    if (toRegister.length === 0) return;

    setSubmitting(true);
    const entries = await Promise.all(
      toRegister.map(async (event) => [
        event.id,
        isRegistrationOpen(event) ? await saveRegistration(formData, userId, event) : { success: false, isClosed: true }
      ])
    );
    const newResults = Object.fromEntries(entries);
    setResults((prev) => ({ ...prev, ...newResults }));

    const summary = toRegister
      .map((event) => `${event.name}: ${newResults[event.id].success ? 'registered' : newResults[event.id].isDuplicate ? 'already registered' : newResults[event.id].isClosed ? 'registration closed' : 'failed'}`)
      .join('. ');
    setStatusMessage(summary);
    setSubmitting(false);
  };

  const anySelected = openEvents.some((e) => selected[e.id] && !results[e.id]);

  return (
    <div className="registration-container">
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">{statusMessage}</div>
      <h2 className="cross-sell-title">Also register for another event?</h2>
      <div className="cross-sell-list">
        {openEvents.map((event) => {
          const result = results[event.id];
          return (
            <label key={event.id} className="checkbox-label">
              <input
                type="checkbox"
                checked={!!selected[event.id]}
                onChange={() => toggle(event.id)}
                disabled={submitting || !!result}
              />
              <span>{event.name}</span>
              {result && (
                <span className={`cross-sell-status ${result.success ? 'success' : 'error'}`}>
                  {result.success ? 'Registered ✓' : result.isDuplicate ? 'Already registered' : result.isClosed ? 'Registration closed' : 'Failed'}
                </span>
              )}
            </label>
          );
        })}
      </div>
      <button
        type="button"
        className="submit-button"
        onClick={handleSubmit}
        disabled={submitting || !anySelected}
      >
        {submitting ? 'Registering...' : 'Register for selected'}
      </button>
    </div>
  );
};

export default CrossSellPrompt;
